import {
  CELL_COLLAPSED,
  CELL_KEY_SEP,
  CELL_WILDCARD,
  ENUM_PROPERTY_VALUES,
  FACTOR_KINDS,
  FactorKind,
  isCharacteristicAllowedForKind,
  isReservedDocumentKey,
  ItemEnumProperty,
  NUMERIC_PROPERTIES,
  PROPERTY_CHARACTERISTICS,
  PropertyCharacteristic,
  PropertyCharacteristicGroup,
  RateConfig,
  RateConfigDocument,
  RateFactor,
  quantityBreakKey
} from "./RateConfig";
import {NUMBER_UP_VALUES} from "./numberUp";
import {computeFactorId} from "./RateFactorId";

/** A single validation issue. `path` is a JSON-pointer-ish string. */
export interface ValidationIssue {
  path: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

const ENUM_CHARACTERISTICS = new Set<PropertyCharacteristic>(['bindingType', 'interiorType', 'laminateType', 'paperType', 'packageType', 'carrierServiceLevel', 'facility']);

/**
 * Pure-functional validator. v3 — see `docs/contracts/shared/RateConfig.md`.
 *
 * Errors block save; warnings are surfaced in the editor but do not block.
 */
export class RateConfigValidator {

  static validate(doc: RateConfigDocument | undefined | null): ValidationResult {
    const errors: ValidationIssue[] = [];
    const warnings: ValidationIssue[] = [];
    if (!doc) {
      warnings.push({path: '/', message: 'Document is empty.', severity: 'warning'});
      return {valid: true, errors, warnings};
    }
    let scopeCount = 0;
    for (const scope of Object.keys(doc)) {
      // Skip reserved base-level fields (editor-only preview axes and
      // the server-facing `facilities` id list). Iterating them as
      // scopes would try to validate a `number[]` as a `RateConfig`
      // and emit spurious "Scope is not an object" errors — and, when
      // `facilities` is present, would also block save every time the
      // editor snapshots the active facility catalog into `doc`.
      if (isReservedDocumentKey(scope)) { continue; }
      scopeCount++;
      const cfg = (doc as any)[scope] as Partial<RateConfig> | undefined;
      RateConfigValidator.validateScope(scope, cfg, errors, warnings);
    }
    if (scopeCount === 0) {
      warnings.push({path: '/', message: 'Document has no scopes. The engine will treat all items as zero-cost.', severity: 'warning'});
    }
    return {valid: errors.length === 0, errors, warnings};
  }

  private static validateScope(scope: string, cfg: Partial<RateConfig> | undefined,
                              errors: ValidationIssue[], warnings: ValidationIssue[]): void {
    const base = `/${scope}`;
    if (!cfg || typeof cfg !== 'object') {
      errors.push({path: base, message: 'Scope is not an object.', severity: 'error'});
      return;
    }
    if (cfg.version !== 3) {
      errors.push({path: `${base}/version`, message: 'version must be 3.', severity: 'error'});
    }
    if (typeof cfg.currency !== 'string' || cfg.currency.length === 0) {
      errors.push({path: `${base}/currency`, message: 'currency must be a non-empty string.', severity: 'error'});
    }
    if (cfg.percentageDiscount != null) {
      const pct = Number(cfg.percentageDiscount);
      if (!isFinite(pct) || pct < -100 || pct > 100) {
        errors.push({path: `${base}/percentageDiscount`, message: 'percentageDiscount must be in [-100, 100].', severity: 'error'});
      }
    }
    if (cfg.defaultQuantityBreaks) {
      RateConfigValidator.validateQuantityBreakTiers(cfg.defaultQuantityBreaks, `${base}/defaultQuantityBreaks`, errors);
    }
    const factors = cfg.factors || [];
    const idCounts = new Map<string, number>();
    for (let i = 0; i < factors.length; i++) {
      RateConfigValidator.validateFactor(factors[i], `${base}/factors/${i}`, cfg, errors, warnings);
      const id = factors[i].id;
      if (id) { idCounts.set(id, (idCounts.get(id) || 0) + 1); }
    }
    for (const [id, n] of idCounts.entries()) {
      if (n > 1) {
        errors.push({path: `${base}/factors`, message: `Duplicate factor id "${id}" (${n} factors share it).`, severity: 'error'});
      }
    }
  }

  private static validateFactor(f: RateFactor, base: string, scopeCfg: Partial<RateConfig>,
                               errors: ValidationIssue[], warnings: ValidationIssue[]): void {
    if (!f || typeof f !== 'object') {
      errors.push({path: base, message: 'Factor is not an object.', severity: 'error'});
      return;
    }
    if (FACTOR_KINDS.indexOf(f.kind) < 0) {
      errors.push({path: `${base}/kind`, message: `kind must be one of ${FACTOR_KINDS.join(', ')}.`, severity: 'error'});
    }
    if (f.kind === 'PER_NUMERIC_PROPERTY') {
      if (!f.property) {
        errors.push({path: `${base}/property`, message: 'PER_NUMERIC_PROPERTY factor requires a property.', severity: 'error'});
      } else if (NUMERIC_PROPERTIES.indexOf(f.property) < 0) {
        errors.push({path: `${base}/property`, message: `property "${f.property}" is not a known numeric property.`, severity: 'error'});
      }
    }
    if (f.kind === 'PER_PACKAGE') {
      // PER_PACKAGE factors should generally group on `packageType`
      // (otherwise every package in `order.packageTypes[]` would
      // resolve to the same cell — equivalent to a `PER_ORDER`
      // factor multiplied by the package count, which is rarely the
      // intent). Soft-warn rather than reject to leave room for a
      // legitimate per-package-count flat fee authored as `groups: []`
      // + `baseAmount`.
      const factorGroups = f.groups || [];
      if (factorGroups.length > 0) {
        const usesPackageType = factorGroups.some(g => (g.characteristics || []).indexOf('packageType') >= 0);
        if (!usesPackageType) {
          warnings.push({
            path: `${base}/groups`,
            message: 'PER_PACKAGE factor with groups does not include `packageType` in any group. The amount will not differentiate by package type; consider PER_ORDER instead.',
            severity: 'warning'
          });
        }
      }
    }
    const expectedId = computeFactorId(f);
    if (f.id !== expectedId) {
      errors.push({
        path: `${base}/id`,
        message: `id "${f.id}" does not match the auto-derived id "${expectedId}". The editor recomputes ids on save; do not hand-author this field.`,
        severity: 'error'
      });
    }
    const groups = f.groups || [];
    if (groups.length === 0) {
      if (f.baseAmount != null && typeof f.baseAmount !== 'number') {
        errors.push({path: `${base}/baseAmount`, message: 'baseAmount must be a number.', severity: 'error'});
      }
    } else {
      if (f.baseAmount != null && f.baseAmount !== 0) {
        warnings.push({path: `${base}/baseAmount`, message: 'baseAmount is ignored when groups[] is non-empty.', severity: 'warning'});
      }
      const seenSets = new Set<string>();
      for (let gi = 0; gi < groups.length; gi++) {
        const g = groups[gi];
        const key = [...(g.characteristics || [])].sort().join(',');
        if (seenSets.has(key)) {
          errors.push({
            path: `${base}/groups/${gi}/characteristics`,
            message: `Two groups must not contain the same SET of characteristics ({${key}}).`,
            severity: 'error'
          });
        }
        seenSets.add(key);
        RateConfigValidator.validateGroup(g, `${base}/groups/${gi}`, scopeCfg, f.kind, errors, warnings);
      }
    }
  }

  private static validateGroup(g: PropertyCharacteristicGroup, base: string, scopeCfg: Partial<RateConfig>,
                              factorKind: FactorKind,
                              errors: ValidationIssue[], warnings: ValidationIssue[]): void {
    if (!g || typeof g !== 'object') {
      errors.push({path: base, message: 'Group is not an object.', severity: 'error'});
      return;
    }
    const chars = g.characteristics || [];
    if (chars.length === 0) {
      errors.push({path: `${base}/characteristics`, message: 'characteristics must be non-empty.', severity: 'error'});
      return;
    }
    const seen = new Set<PropertyCharacteristic>();
    for (let i = 0; i < chars.length; i++) {
      if (PROPERTY_CHARACTERISTICS.indexOf(chars[i]) < 0) {
        errors.push({path: `${base}/characteristics/${i}`, message: `"${chars[i]}" is not a known propertyCharacteristic.`, severity: 'error'});
      } else if (!isCharacteristicAllowedForKind(factorKind, chars[i])) {
        errors.push({
          path: `${base}/characteristics/${i}`,
          message: `"${chars[i]}" is not a legal characteristic for a ${factorKind} factor.`,
          severity: 'error'
        });
      }
      if (seen.has(chars[i])) {
        errors.push({path: `${base}/characteristics/${i}`, message: `"${chars[i]}" is repeated within this group.`, severity: 'error'});
      }
      seen.add(chars[i]);
    }
    const cells = g.cells || {};
    const keys = Object.keys(cells);
    if (keys.length === 0) {
      errors.push({path: `${base}/cells`, message: 'cells must be non-empty.', severity: 'error'});
      return;
    }
    const allowedTokens = chars.map(c => allowedTokensFor(c, scopeCfg));
    let allWildcardSeen = false;
    let nonWildcardSeen = false;
    for (const k of keys) {
      const tokens = k.split(CELL_KEY_SEP);
      if (tokens.length !== chars.length) {
        errors.push({path: `${base}/cells/${k}`, message: `cell key has ${tokens.length} tokens but the group has ${chars.length} characteristics.`, severity: 'error'});
        continue;
      }
      let everyWildcard = true;
      for (let i = 0; i < tokens.length; i++) {
        const tok = tokens[i];
        if (tok === CELL_WILDCARD || tok === CELL_COLLAPSED) { continue; }
        everyWildcard = false;
        const allowed = allowedTokens[i];
        if (allowed && allowed.indexOf(tok) < 0) {
          errors.push({
            path: `${base}/cells/${k}`,
            message: `Token "${tok}" at position ${i} (characteristic "${chars[i]}") is not a recognized value. Expected one of: ${allowed.join(', ')} or "*".`,
            severity: 'error'
          });
        }
      }
      if (everyWildcard) { allWildcardSeen = true; } else { nonWildcardSeen = true; }
      const cell = cells[k];
      if (!cell || typeof cell !== 'object') {
        errors.push({path: `${base}/cells/${k}`, message: 'cell value must be an object.', severity: 'error'});
        continue;
      }
      if (cell.infeasible && cell.amount != null && cell.amount !== 0) {
        warnings.push({path: `${base}/cells/${k}`, message: 'cell.amount is ignored when infeasible:true.', severity: 'warning'});
      }
      if (!cell.infeasible && (typeof cell.amount !== 'number' || !isFinite(cell.amount))) {
        errors.push({path: `${base}/cells/${k}`, message: 'non-infeasible cell must have a numeric amount.', severity: 'error'});
      }
    }
    if (!allWildcardSeen && nonWildcardSeen) {
      warnings.push({
        path: `${base}/cells`,
        message: 'Group has no all-wildcard cell. If the order item carries a value not present in cells[], the group contributes nothing. Add `' + chars.map(() => '*').join(CELL_KEY_SEP) + '` as a fallback.',
        severity: 'warning'
      });
    }
    if (allWildcardSeen && chars.length > 1 && !nonWildcardSeen) {
      warnings.push({
        path: `${base}/cells`,
        message: 'Group only has an all-wildcard cell. The propertyCharacteristics in this group are not differentiating anything; this group could be replaced by `baseAmount`.',
        severity: 'warning'
      });
    }
  }

  private static validateQuantityBreakTiers(tiers: { minQty: number; maxQty: number }[], base: string,
                                            errors: ValidationIssue[]): void {
    let last = 0;
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (typeof t.minQty !== 'number' || typeof t.maxQty !== 'number') {
        errors.push({path: `${base}/${i}`, message: 'tier minQty and maxQty must be numbers.', severity: 'error'});
        continue;
      }
      if (i > 0 && t.minQty <= last) {
        errors.push({path: `${base}/${i}`, message: 'tiers must be non-overlapping and ascending.', severity: 'error'});
      }
      if (t.maxQty < t.minQty) {
        errors.push({path: `${base}/${i}`, message: 'tier maxQty must be ≥ minQty.', severity: 'error'});
      }
      last = t.maxQty;
    }
  }
}

function allowedTokensFor(c: PropertyCharacteristic, cfg: Partial<RateConfig>): string[] | undefined {
  if (ENUM_CHARACTERISTICS.has(c)) {
    const enumProp = c as ItemEnumProperty;
    const values = ENUM_PROPERTY_VALUES[enumProp];
    // paperType / packageType / carrierServiceLevel / facility are
    // dynamic: their value sets are sourced at runtime from the server
    // (PaperType / PackageType tables, CarrierService catalog,
    // FacilityService). The validator can't enforce a literal
    // allow-list for them, so it accepts any token at those positions;
    // coverage / wildcard / collapsed-token checks still apply.
    if (enumProp === 'paperType' || enumProp === 'packageType' || enumProp === 'carrierServiceLevel' || enumProp === 'facility') { return undefined; }
    return values && values.length > 0 ? [...values] : undefined;
  }
  if (c === 'numberUp') { return [...NUMBER_UP_VALUES]; }
  if (c === 'quantityBreak') {
    const tiers = (cfg && cfg.defaultQuantityBreaks && cfg.defaultQuantityBreaks.length > 0)
      ? cfg.defaultQuantityBreaks
      : [];
    return tiers.length > 0 ? tiers.map(quantityBreakKey) : undefined;
  }
  return undefined;
}

/**
 * Returns true if some "ancestor" of `tokens` (obtained by replacing one
 * or more non-wildcard positions with `*`) exists in `valuedCanonicals`.
 * Used to verify that an empty cell has a wider-wildcard cell to fall
 * back to. The ancestor relation is reflexive in matching but not for
 * fallback purposes — a key is NOT considered its own ancestor here.
 */
function hasValuedAncestor(tokens: string[], valuedCanonicals: Set<string>): boolean {
  const n = tokens.length;
  // Iterate non-empty subsets of positions to wildcard; we only consider
  // positions that aren't already '*'.
  const variable: number[] = [];
  for (let i = 0; i < n; i++) { if (tokens[i] !== CELL_WILDCARD) { variable.push(i); } }
  const m = variable.length;
  if (m === 0) {
    // The cell is already all-wildcard; nothing wider exists. Treat as
    // "no fallback" so the validator surfaces it.
    return false;
  }
  const total = 1 << m;
  for (let mask = 1; mask < total; mask++) { // mask=0 → no change; skip
    const candidate = tokens.slice();
    for (let j = 0; j < m; j++) {
      if (mask & (1 << j)) { candidate[variable[j]] = CELL_WILDCARD; }
    }
    if (valuedCanonicals.has(candidate.join(CELL_KEY_SEP))) { return true; }
  }
  return false;
}

